import {
    TabStrip,
    TabStripSelectEventArguments,
    TabStripTab,
} from '@progress/kendo-react-layout'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { AuthContext, useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { useState, useEffect, ReactElement } from 'react'
import {
    DEFAULT_LOCATION,
    MembershipItem,
    MembershipType,
    RedemptionUnit,
    ValidityTypes,
} from 'types/membership'
import { Tenant } from 'types/tenant'
import { classNames } from 'utils/dom-helpers'
import { postApiRequest, putApiRequest, useFetch } from 'utils/fetch-helper'
import { FetchMethods } from 'utils/types'
import DialogModal from '../Dialog'
import { createModal } from '../ModalFactory'
import { ModalWrapper } from '../ModalWrapper'
import { ModalDataAttributes } from '../types'
import BasicInfo from './BasicInfo'
import MembershipBookingPage from './BookingPage'
import Members from './Members'
import RedemptionDetails from './RedemptionDetails'
import SelectServices from './SelectServices'

const ModalProvider = createModal(
    AuthContext,
    'MembershipModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <span className="circular">Create</span>
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-chevron-left " />
        </span>
    )
)

export const MembershipModalOpener = ModalProvider.Opener
export const MembershipModalCloser = ModalProvider.Closer

function MembershipModal() {
    const { MembershipModal } = useAuth()
    const { tenant } = useTenantContext()
    const { attributes, setAttributes } = MembershipModal
    const { translations, loadComponentTranslations } = useAppContext()
    const [selected, setSelected] = useState<number>(0)
    const [disableBtn, setDisableBtn] = useState(false)
    const handleSelect = (e: TabStripSelectEventArguments) => {
        setSelected(e.selected)
    }
    const [prompt_message, toggleDialog] = useState<string | ReactElement>(
        ''
    )
    const [fatal_message, setFatalMessage] = useState('')

    const {
        data: categories,
        status: categories_api_status,
        doFetch: getCategories,
    } = useFetch(
        `/v1/categories/by-type?size=500&type=MEMBERSHIP&tenantId=${tenant?.id}`,
        FetchMethods.GET,
        false
    )

    const {
        data: locations,
        status: locations_api_status,
        doFetch: getLocations,
    } = useFetch(
        `/v1/locations/tenant-id?size=500&tenantId=${tenant?.id}&isDeleted=false`,
        FetchMethods.GET,
        false
    )

    const {
        data: services,
        status: services_api_status,
        doFetch: getServices,
    } = useFetch(
        `/v1/services?size=500&tenantId=${tenant?.id}&isDeleted=false`,
        FetchMethods.GET,
        false
    )

    useEffect(() => {
        if (MembershipModal.is_open) {
            setSelected(0)
            getCategories()
            getLocations()
            getServices()

            if (attributes?.is_new) {
                handleItemAdd()
            } else if (attributes?.is_edit && attributes?.edit_membership) {
                handleItemEdit(
                    attributes?.edit_membership as MembershipItem
                )
            }
        }
    }, [MembershipModal.is_open])

    // Membership Category
    useEffect(() => {
        if (
            categories_api_status == 200 &&
            categories.content &&
            categories.content.length > 0
        ) {
            let fetched_categories = categories.content.map(
                (category: Record<string, string>) => {
                    return {
                        text: category.name,
                        value: category.id,
                    }
                }
            )
            setAttributes({
                ...attributes,
                membership_categories: fetched_categories,
            })
        }
    }, [categories, categories_api_status])

    // Locations
    useEffect(() => {
        if (locations_api_status == 200) {
            if (locations.content.length == 0) {
                setFatalMessage(
                    'No Location has been created yet. Please create one.'
                )
            } else {
                let fetched_locations = locations.content.map(
                    (location: Record<string, string>) => {
                        return {
                            text: location.name,
                            value: location.id,
                        }
                    }
                )

                let primary_location =
                    DEFAULT_LOCATION as ModalDataAttributes
                if (attributes?.primary_location_id) {
                    primary_location = fetched_locations.find(
                        (l: Record<string, string>) =>
                            l.value == attributes?.primary_location_id
                    ) as ModalDataAttributes
                }

                let additional_locations = [] as ModalDataAttributes[]
                if (attributes?.location_ids) {
                    const location_ids =
                        attributes?.location_ids as string[]
                    if (location_ids.length == fetched_locations.length) {
                        additional_locations = [DEFAULT_LOCATION]
                    } else {
                        additional_locations = fetched_locations.filter(
                            (l: Record<string, string>) => {
                                return (
                                    l.value != primary_location?.value &&
                                    location_ids.indexOf(l.value) > -1
                                )
                            }
                        ) as ModalDataAttributes[]
                    }
                }

                fetched_locations.unshift(DEFAULT_LOCATION)

                setAttributes({
                    ...attributes,
                    primary_location,
                    additional_locations,
                    locations: fetched_locations,
                })
            }
        }
    }, [locations, locations_api_status])

    // Services
    useEffect(() => {
        if (services_api_status == 200) {
            if (services.content.length == 0) {
                setFatalMessage(
                    'No Service has been created yet. Please create one.'
                )
            } else {
                let fetched_services = services.content.map(
                    (service: Record<string, string>) => {
                        return service
                    }
                )
                setAttributes({
                    ...attributes,
                    services: fetched_services,
                })
            }
        }
    }, [services, services_api_status])

    useEffect(() => {
        if (!translations.MEMBERSHIPS_TRANSLATION_ID) {
            loadComponentTranslations('MEMBERSHIPS_TRANSLATION_ID')
        }
    }, [translations])

    function handleItemAdd() {
        setAttributes({
            ...attributes,
            membership_type: MembershipType.Package,
            primary_location: DEFAULT_LOCATION,
            redemption_unit: RedemptionUnit.CLASSES,
            is_public: true,
            validity_type: ValidityTypes[0].value,
        })
    }

    function handleItemEdit(membership: MembershipItem) {
        // Function to generate the copy name based on how many copies have been made
        const generateCopyName = (baseName: string): string => {
            // If baseName is undefined, use an empty string or a fallback value
            if (!baseName) {
                return 'Unnamed Membership (COPY1)'; // Fallback name in case it's undefined
            }

            const regex = /\(COPY(\d*)\)$/; // Matches (COPY), (COPY1), (COPY2), etc.
            const match = baseName.match(regex);

            if (match) {
                const copyNumber = match[1] ? parseInt(match[1]) + 1 : 1;  // Increment the copy number
                return baseName.replace(regex, `(COPY${copyNumber})`); // Replace the old copy number with the new one
            } else {
                return baseName + ' (COPY1)'; // If no match, it's the first copy
            }
        };
    
        if (attributes?.is_copy_flag == 'copy') {
            // Generate the new copy name based on the original name
            const newName = generateCopyName(membership?.name || ' ');
    
            delete membership.id;  // Remove the ID to ensure it's treated as a new copy
    
            setAttributes({
                ...attributes,
                ...membership,
                name: newName,  // Update the name with the generated copy name
                selected_categories: membership.category_ids as string[],
                selected_services: membership.service_ids as string[],
                validity_value: membership.membership_package?.validity_value as number,
                validity_type: membership.membership_package?.validity_type as string,
                cycles: membership.membership_subscription?.number_of_cycles as number,
                renewal: membership.membership_subscription?.renewal_action as string,
                time_period: membership.membership_subscription?.time_period as string,
                allow_unlimited: membership.redemption_services == null,
                service_discount_percentage: membership.service_discount_percentage,
                apply_member_discount: membership.service_discount_percentage != undefined,
            });
        } else {
            setAttributes({
                ...attributes,
                ...membership,
                selected_categories: membership.category_ids as string[],
                selected_services: membership.service_ids as string[],
                validity_value: membership.membership_package?.validity_value as number,
                validity_type: membership.membership_package?.validity_type as string,
                cycles: membership.membership_subscription?.number_of_cycles as number,
                renewal: membership.membership_subscription?.renewal_action as string,
                time_period: membership.membership_subscription?.time_period as string,
                service_discount_percentage: membership.service_discount_percentage,
                apply_member_discount: membership.service_discount_percentage != undefined,
            });
        }
    }

    const onSubmit = async () => {
        const {
            id,
            group_id,
            custom_slug,
            image_url,
            version,
            membership_type,
            name,
            description,
            category_id,
            price,
            initial_fee,
            validity_value,
            validity_type,
            cycles,
            renewal,
            time_period,
            selected_categories,
            selected_services,
            is_public,
            redemption_unit,
            allow_unlimited,
            service_discount_percentage
        } = attributes || {}

        let redemption_amount = 0
        let redemption_minutes = 0
        let redemption_services: number | null = 0

        switch (redemption_unit) {
            case RedemptionUnit.CLASSES:
                redemption_services =
                    allow_unlimited
                        ? null
                        : attributes?.redemption_services as number
                break
            case RedemptionUnit.AMOUNT:
                redemption_amount = attributes?.redemption_amount as number
                break
            case RedemptionUnit.TIME:
                redemption_minutes =
                    attributes?.redemption_minutes as number
                break
        }

        let membership: any = {
            id: id,
            groupId: group_id,
            customSlug: custom_slug,
            imageUrl: image_url,
            version: version,
            additionalFee: initial_fee,
            categoryId: category_id,
            categoryIds: selected_categories,
            description: description,
            locationIds: [],
            name: name,
            price: price || 0,
            public: is_public,
            redemptionAmount: redemption_amount,
            redemptionMinutes: redemption_minutes,
            redemptionServices: redemption_services,
            serviceDiscountPercentage: attributes?.apply_member_discount ? service_discount_percentage : undefined,
            serviceIds: selected_services,
            tenantId: tenant?.id,
        }

        let primary_location = attributes?.primary_location as Record<
            string,
            string
        >
        if (primary_location != DEFAULT_LOCATION) {
            membership.primaryLocationId = primary_location.value
            membership.locationIds.push(primary_location.value)
        }

        let additional_locations =
            attributes?.additional_locations as ModalDataAttributes[]
        if (additional_locations[0] == DEFAULT_LOCATION) {
            membership.locationIds = (
                attributes?.locations as ModalDataAttributes[]
            )
                .filter((i) => i != DEFAULT_LOCATION)
                .map((i) => i.value)
        } else {
            additional_locations.forEach((location) => {
                membership.locationIds.push(location.value)
            })
        }

        if (membership_type == MembershipType.Package) {
            membership.membershipPackage = {
                validityType: validity_type,
                validityValue: validity_value,
            }
        } else {
            membership.membershipSubscription = {
                minimumRenewals: 0,
                numberOfCycles: cycles,
                renewalAction: renewal,
                timePeriod: time_period,
                validRenewals: renewal == 'ROLLOVER' ? 1 : 0,
            }
        }

        let api = membership.id
            ? await putApiRequest(
                `/v1/memberships/${attributes?.id}`,
                membership
            )
            : await postApiRequest(`/v1/memberships`, membership)

        if (api.id) {
            setAttributes({
                ...attributes,
                refetch: true,
            })
            MembershipModal.close()
        } else {
            toggleDialog(api.message)
        }
    }

    return (
        <ModalProvider.Visible>
            <ModalWrapper>
                <div className="bg-white shadow-3xl w-full overflow-x-hidden sm:rounded-md mx-auto my-4 relative border-2 border-gray-100 flex flex-col">
                    <div className="flex px-4 text-gray-500 z-10 w-full pt-4 bg-opacity-30 pb-3 bg-primary-lighter">
                        <div>
                            <ul className="ml-6 text-xs flex gap-1 breadcrumbs text-gray-400">
                                <li>Dashboard</li>
                                <li>Memberships</li>
                            </ul>
                            <span className="inline-block self-center text-lg text-primary-dark">
                                <MembershipModalCloser className="self-center" />
                                <span className="circular">
                                    {attributes?.id ? (
                                        attributes?.name
                                    ) : (
                                        <Translation content_key="new_membership" />
                                    )}
                                </span>
                            </span>
                        </div>
                    </div>
                    {fatal_message ? (
                        <div className="relative flex flex-col">
                            <div className="justify-center text-center flex flex-col gap-5 p-12">
                                <p className="text-primary text-xl font-medium font-display">
                                    {fatal_message}
                                </p>
                                <button
                                    className={classNames(
                                        'group relative flex justify-center w-40',
                                        'py-3 px-12 mx-auto border border-transparent',
                                        'rounded-md text-white',
                                        'focus:outline-none',
                                        'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                                    )}
                                    onClick={() => {
                                        MembershipModal.close()
                                    }}
                                >
                                    <Translation content_key="close" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <TabStrip
                        selected={selected}
                        onSelect={handleSelect}
                        className="z-20"
                        animation={false}
                    >
                        <TabStripTab
                            title={<Translation content_key="basic_info_panel_title" />}
                            contentClassName="p-5 "
                        >
                            <BasicInfo
                                attributes={attributes}
                                tenant={tenant as Tenant}
                                setAttributes={setAttributes}
                                translations={translations}
                                onCancel={() => MembershipModal.close()}
                                onNext={() => setSelected(1)}
                            />
                        </TabStripTab>
                    
                        <TabStripTab
                            title={<Translation content_key="redemption_details" />}
                            contentClassName="p-5 redemption-details membership-wrap"
                        >
                            <RedemptionDetails
                                attributes={attributes}
                                setAttributes={setAttributes}
                                onCancel={() => MembershipModal.close()}
                                onPrevious={() => setSelected(0)}
                                onNext={() => setSelected(2)}
                                disableBtn={disableBtn}
                                setDisableBtn={setDisableBtn}
                            />
                        </TabStripTab>
                    
                        <TabStripTab
                            disabled={disableBtn}
                            title={<Translation content_key="select_services" />}
                            contentClassName="p-5 membership-wrap select-servies-membership"
                        >
                            <SelectServices
                                attributes={attributes}
                                setAttributes={setAttributes}
                                translations={translations}
                                onCancel={() => MembershipModal.close()}
                                onPrevious={() => setSelected(1)}
                                onNext={() => setSelected(3)}
                            />
                        </TabStripTab>
                    
                        <TabStripTab
                            disabled={disableBtn}
                            title={<Translation content_key="booking_page" />}
                            contentClassName="p-5 membership-wrap booking-membership"
                        >
                            <MembershipBookingPage
                                attributes={attributes}
                                setAttributes={setAttributes}
                                translations={translations}
                                onCancel={() => MembershipModal.close()}
                                onPrevious={() => setSelected(2)}
                                onSubmit={onSubmit}
                            />
                        </TabStripTab>
                    
                        {
                            !attributes?.is_new && // Only show this tab if not in 'new' mode
                            <TabStripTab
                                disabled={disableBtn}
                                title={<Translation content_key="members" />}
                                contentClassName="p-5 membership-wrap members-membership"
                            >
                                <Members
                                    attributes={attributes}
                                    setAttributes={setAttributes}
                                    onCancel={() => MembershipModal.close()}
                                    onPrevious={() => setSelected(4)}
                                />
                            </TabStripTab>
                        }
                    </TabStrip>
                    
                    )}
                </div>
                <DialogModal
                    prompt_message={prompt_message}
                    onCloseModal={() => {
                        toggleDialog('')
                    }}
                />
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}

export default MembershipModal
